
import React from "react";
import { Event } from "@/types";
import { 
  Card, 
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle 
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { CalendarIcon, MapPinIcon, UserIcon, ExternalLinkIcon } from "lucide-react";
import { format, parseISO } from "date-fns";
import { Button } from "@/components/ui/button";

interface EventCardProps {
  event: Event;
}

const EventCard = ({ event }: EventCardProps) => {
  // Ensure dates are properly parsed
  const startDate = parseISO(event.start_date);
  const endDate = parseISO(event.end_date);
  
  // Format date display
  const isSameDay = startDate.toDateString() === endDate.toDateString();
  
  // Create date display text
  const dateDisplayText = isSameDay 
    ? `${format(startDate, "PPP")} from ${format(startDate, "h:mm a")} to ${format(endDate, "h:mm a")}`
    : `${format(startDate, "PPP")} at ${format(startDate, "h:mm a")} to ${format(endDate, "PPP")} at ${format(endDate, "h:mm a")}`;
  
  return (
    <Card key={event.id}>
      <CardHeader>
        <div className="flex justify-between items-start">
          <div>
            <CardTitle>{event.title}</CardTitle>
            <CardDescription className="flex items-center gap-1 mt-1">
              <CalendarIcon className="h-4 w-4" />
              <span>{dateDisplayText}</span>
            </CardDescription>
          </div>
          {event.category && (
            <Badge variant="outline" className="bg-eco-100 text-eco-800">
              {event.category}
            </Badge>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {event.description && (
          <p className="text-sm text-muted-foreground mb-4 whitespace-pre-line">
            {event.description.length > 200 
              ? `${event.description.slice(0, 200)}...` 
              : event.description}
          </p>
        )}
        <div className="flex flex-col gap-2 text-sm">
          {event.location && (
            <div className="flex items-center gap-2">
              <MapPinIcon className="h-4 w-4 text-muted-foreground" />
              <span>{event.location}</span>
            </div>
          )}
          {event.organizer && (
            <div className="flex items-center gap-2">
              <UserIcon className="h-4 w-4 text-muted-foreground" />
              <span>Organized by: {event.organizer}</span>
            </div>
          )}
        </div>
      </CardContent>
      <CardFooter className="flex justify-between">
        <div className="text-sm text-muted-foreground">
          {format(startDate, "PPP")} - {format(endDate, "PPP")}
        </div>
        {event.website && (
          <Button variant="ghost" size="sm" asChild>
            <a 
              href={event.website} 
              target="_blank" 
              rel="noopener noreferrer"
              className="flex items-center gap-1 text-eco-500"
            >
              Visit <ExternalLinkIcon className="h-3 w-3" />
            </a>
          </Button>
        )}
      </CardFooter>
    </Card>
  );
};

export default EventCard;
